/**
 * useStudio Hook
 *
 * Provides Studio navigation page data from static content.
 */

import { QUERY_CONFIG } from '@/@admin/types/constants';
import { NavigationPage } from '@/@cms/types';
import { CUSTOM_DASHBOARD_SLUG } from '@/@dashboards/types/constants';
import { studioService } from '@/@studio/services';
import { QUERY_KEYS } from '@/@studio/types/constants';
import { tracking } from '@/lib/features/tracking';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

export interface UseStudioOptions {
    /** Optional slug to filter navigation pages */
    navigationSlug?: string;
    /** Whether to enable the navigation query */
    enableNavigationQuery?: boolean;
}

export function useStudio({ navigationSlug, enableNavigationQuery = true }: UseStudioOptions = {}) {
    const getNavigationPage = useCallback(async () => {
        if (!navigationSlug) {
            return {
                Header: [],
                Sections: [],
            } as NavigationPage;
        }

        if (navigationSlug === CUSTOM_DASHBOARD_SLUG) {
            return {
                Header: [],
                Sections: [],
            } as NavigationPage;
        }

        try {
            return await studioService.getNavigationPage(navigationSlug);
        } catch (error) {
            tracking.errorWithSource(
                error as Error,
                `studio.useStudio.getNavigationPage.${navigationSlug}`,
            );
            throw error;
        }
    }, [navigationSlug]);

    const navigationQuery = useQuery<NavigationPage>({
        queryKey: QUERY_KEYS.NAVIGATION(navigationSlug),
        queryFn: getNavigationPage,
        staleTime: QUERY_CONFIG.STALE_TIME,
        gcTime: QUERY_CONFIG.GC_TIME,
        enabled: !!navigationSlug && enableNavigationQuery,
    });

    return {
        navigation: navigationQuery.data,
        isLoadingNavigation: navigationQuery.isLoading,
        isErrorNavigation: navigationQuery.isError,
        navigationError: navigationQuery.error,
        refetchNavigation: navigationQuery.refetch,
    };
}
